import {
	BodyScrollEndEvent,
	ColDef,
	FirstDataRenderedEvent,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject, useCallback, useMemo, useRef, useState } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useDescText } from "src/hooks/useDescText";
import { DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	restoreGridScrollState,
	saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";

import {
	defaultKSK2140_R01DetailDto,
	KSK2140_R01DetailDto,
} from "./Dto_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";

const COLUMN_STATE_KEY = "KSK2140_R01-detail-grid-column-state-v4";

interface DetailTableKSK2140_R01Props {
	gridRef: RefObject<AgGridReact<KSK2140_R01DetailDto> | null>;
}

export const DetailTable_KSK2140_R01 = ({ gridRef }: DetailTableKSK2140_R01Props) => {
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const { renderDescText } = useDescText();
	const inspectionCompleteText = renderDescText(DESC_FLD.INSPECTION_COMPLETE, "검사완료");
	const uninspectedText = renderDescText(DESC_FLD.UNINSPECTED, "미검사");
	const countTemplate = renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건");
	const noDataOverlay = `<span style="padding: 10px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`;
	const {
		KSK2140_R01DetailList,
		selectedKSK2140_R01DetailList,
		setSelectedKSK2140_R01DetailList,
		initSelectedKSK2140_R01DetailList,
		deletedKSK2140_R01DetailList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useKSK2140_R01Store();

	const {
		getRowId,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent<KSK2140_R01DetailDto>({
		gridRef,
		key: "valueIndex",
		gridData: KSK2140_R01DetailList,
		defaultGridData: defaultKSK2140_R01DetailDto,
		selectedRow: selectedKSK2140_R01DetailList ?? undefined,
	});

	const getCellStyle = (params: any, key: string) => {
		return onlyTouchGridStyle(key, "itemCd")(params);
	};

	const formatQty = (params: any) =>
		params.node?.rowPinned && params.value == null ? "" : Number(params.value ?? 0).toLocaleString();

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		itemCd: renderDescText(DESC_FLD.ITEM_CODE, "품목코드"),
		itemNm: renderDescText(DESC_FLD.ITEM_NAME, "품명"),
		inspectQty: renderDescText(DESC_FLD.INSPECTION_QTY, "검사량"),
		lotQty: renderDescText(DESC_FLD.UNINSPECTED_QTY, "미검사량"),
		processLotQty: renderDescText(DESC_FLD.ACTUAL_QTY, "실적량"),
		basLotNo: renderDescText(DESC_FLD.LOT_NO, "LOT 번호"),
		lotCreateDt: renderDescText(DESC_FLD.INBOUND_DATE, "최초 입고일"),
		acceptQty: renderDescText(DESC_FLD.ACCEPTED_QTY, "합격량"),
		packQty: renderDescText(DESC_FLD.PACK_QTY, "포장량"),
		checkFinish: renderDescText(DESC_FLD.INSPECTION_STATUS, "검사여부"),
	}), [renderDescText]);

	const baseColumnDefs = useMemo<ColDef<KSK2140_R01DetailDto>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: renderDescText(DESC_FLD.ITEM_CODE, "품목코드"), colId: "itemCd", field: "itemCd", width: 130,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "itemCd"),
		},
		{
			headerName: renderDescText(DESC_FLD.ITEM_NAME, "품명"), colId: "itemNm", field: "itemNm", width: 150,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "itemNm"),
		},
		{
			headerName: renderDescText(DESC_FLD.INSPECTION_STATUS, "검사여부"), colId: "checkFinish", field: "checkFinish", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "checkFinish"),
			valueFormatter: (params: any) =>
				params.node?.rowPinned
					? ""
					: params.data?.checkFinish === "Y"
						? inspectionCompleteText
						: uninspectedText,
		},
		{
			headerName: renderDescText(DESC_FLD.UNINSPECTED_QTY, "미검사량"), colId: "lotQty", field: "lotQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "lotQty"),
			valueFormatter: formatQty,
		},
		{
			headerName: renderDescText(DESC_FLD.LOT_NO, "LOT 번호"), colId: "basLotNo", field: "basLotNo", width: 150,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "basLotNo"),
		},
		{
			headerName: renderDescText(DESC_FLD.ACCEPTED_QTY, "합격량"), colId: "acceptQty", field: "acceptQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "acceptQty"),
			valueFormatter: formatQty,
		},
		{
			headerName: renderDescText(DESC_FLD.PACK_QTY, "포장량"), colId: "packQty", field: "packQty", width: 90,
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, "packQty"),
			valueFormatter: formatQty,
		},
	], [hoveredRowIndex, KSK2140_R01DetailList, inspectionCompleteText, renderDescText, uninspectedText]);

	const columnDefs = useMemo<ColDef<KSK2140_R01DetailDto>[]>(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<KSK2140_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	const pinnedBottomRowData = useMemo<KSK2140_R01DetailDto[]>(() => [
		{
			...defaultKSK2140_R01DetailDto,
			itemCd: formatDescCount(KSK2140_R01DetailList.length, countTemplate),
			lotQty: KSK2140_R01DetailList.reduce((sum, item) => sum + Number(item.lotQty || 0), 0),
			acceptQty: KSK2140_R01DetailList.reduce((sum, item) => sum + Number(item.acceptQty || 0), 0),
			packQty: KSK2140_R01DetailList.reduce((sum, item) => sum + Number(item.packQty || 0), 0),
		},
	], [KSK2140_R01DetailList, countTemplate]);

	const handleRowClick = useCallback((event: RowClickedEvent<KSK2140_R01DetailDto>) => {
		if (event.node.rowPinned === "bottom") return;
		const rowData = event.data;
		if (!rowData) return;

		const valueIndex = String(rowData.valueIndex);
		if (deletedKSK2140_R01DetailList.includes(valueIndex)) return;

		const isSelected = selectedKSK2140_R01DetailList?.valueIndex === rowData.valueIndex;
		if (isSelected) {
			setSelectedKSK2140_R01DetailList(null);
			return;
		}

		setSelectedKSK2140_R01DetailList(rowData);
	}, [
		deletedKSK2140_R01DetailList,
		selectedKSK2140_R01DetailList,
		setSelectedKSK2140_R01DetailList,
	]);

	const handleBodyScrollEnd = (event: BodyScrollEndEvent<KSK2140_R01DetailDto>) => {
		if (event.direction === "vertical") saveGridScrollState(event.api, setDetailScrollTop);
	};

	const handleFirstDataRendered = (event: FirstDataRenderedEvent<KSK2140_R01DetailDto>) => {
		restoreGridScrollState(event.api, detailScrollTop);
	};

	return (
		<GridContextWrapper<KSK2140_R01DetailDto>
			gridRef={gridRef}
			gridWrapperRef={gridWrapperRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedKSK2140_R01DetailList}
			defaultRowData={defaultKSK2140_R01DetailDto}
			baseColumnDefs={columnDefs}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			enableDataHistory={false}
		>
			<Grid_Touch<KSK2140_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				overlayNoRowsTemplate={noDataOverlay}
				defaultColDef={{
					...defaultColDef,
				}}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<KSK2140_R01DetailDto>({
					idKey: "valueIndex",
					selectedList: selectedKSK2140_R01DetailList ? [selectedKSK2140_R01DetailList] : [],
					deletedList: deletedKSK2140_R01DetailList,
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				onBodyScrollEnd={handleBodyScrollEnd}
				onFirstDataRendered={handleFirstDataRendered}
				pinnedBottomRowData={pinnedBottomRowData}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
			/>
		</GridContextWrapper>
	);
};
